import { ragApi } from './ApiService'
import { log } from '@/lib/logger'
import type {
  Agent,
  AgentConnection,
  AgentConnectionStatus,
  AgentCreatePayload,
  AgentUpdatePayload,
  AgentToolsResponse,
  AgentDocumentsResponse,
  DocumentPermission,
  AvailableDocumentsResponse,
  AgentAvailableDocument,
  AgentAvailableModelsResponse,
  ProviderCredentialStatus,
  ProviderCredentialProvider,
  Ability,
  Skill,
  SkillPayload,
} from '@/types/agent'

type PaginatedResponse<T> = {
  results: T[]
  count?: number
}

type AssignResponse = {
  success: boolean
  message: string
}

type AgentCoverResponse = {
  image_url: string
}

class AgentsService {
  private serviceName = 'AgentsService'
  private apiBase = '/api/agent/v1'

  private unwrapData<T>(response: unknown): T {
    if (
      response &&
      typeof response === 'object' &&
      'success' in response &&
      'data' in response
    ) {
      return (response as { data: T }).data
    }
    return response as T
  }

  private extractResults<T>(response: unknown): T[] {
    const normalized = this.unwrapData<unknown>(response)

    if (normalized && typeof normalized === 'object' && 'items' in normalized) {
      return (normalized as { items: T[] }).items
    }
    if (normalized && typeof normalized === 'object' && 'results' in normalized) {
      return (normalized as PaginatedResponse<T>).results
    }
    return Array.isArray(normalized) ? (normalized as T[]) : []
  }

  async listAgents(params?: { enabled?: boolean }): Promise<Agent[]> {
    try {
      const response = await ragApi.get(`${this.apiBase}/agents/`, {
        params: { enabled: params?.enabled },
      })
      return this.extractResults<Agent>(response)
    } catch (error) {
      log.error(`${this.serviceName} listAgents failed`, error, { params })
      throw error
    }
  }

  async getAgent(agentId: string): Promise<Agent> {
    try {
      return this.unwrapData<Agent>(
        await ragApi.get(`${this.apiBase}/agents/${agentId}/`, {
          cache: 'no-store',
        })
      )
    } catch (error) {
      log.error(`${this.serviceName} getAgent failed`, error, { agentId })
      throw error
    }
  }

  async createAgent(payload: AgentCreatePayload): Promise<Agent> {
    try {
      return this.unwrapData<Agent>(await ragApi.post(`${this.apiBase}/agents/`, payload))
    } catch (error) {
      log.error(`${this.serviceName} createAgent failed`, error, { payload })
      throw error
    }
  }

  async updateAgent(agentId: string, payload: AgentUpdatePayload): Promise<Agent> {
    try {
      return this.unwrapData<Agent>(await ragApi.patch(`${this.apiBase}/agents/${agentId}/`, payload))
    } catch (error) {
      log.error(`${this.serviceName} updateAgent failed`, error, { agentId, payload })
      throw error
    }
  }

  async deleteAgent(agentId: string): Promise<void> {
    try {
      await ragApi.delete(`${this.apiBase}/agents/${agentId}/`)
    } catch (error) {
      log.error(`${this.serviceName} deleteAgent failed`, error, { agentId })
      throw error
    }
  }

  async enableAgent(agentId: string): Promise<AssignResponse> {
    try {
      const data = this.unwrapData<Agent>(await ragApi.post(`${this.apiBase}/agents/${agentId}/enable/`))
      return { success: true, message: `Agent ${data.id} enabled` }
    } catch (error) {
      log.error(`${this.serviceName} enableAgent failed`, error, { agentId })
      throw error
    }
  }

  async disableAgent(agentId: string): Promise<AssignResponse> {
    try {
      const data = this.unwrapData<Agent>(await ragApi.post(`${this.apiBase}/agents/${agentId}/disable/`))
      return { success: true, message: `Agent ${data.id} disabled` }
    } catch (error) {
      log.error(`${this.serviceName} disableAgent failed`, error, { agentId })
      throw error
    }
  }

  async getAgentTools(agentId: string): Promise<AgentToolsResponse> {
    try {
      return this.unwrapData<AgentToolsResponse>(await ragApi.get(`${this.apiBase}/agents/${agentId}/tools/`))
    } catch (error) {
      log.error(`${this.serviceName} getAgentTools failed`, error, { agentId })
      throw error
    }
  }

  async getAgentDocuments(agentId: string): Promise<AgentDocumentsResponse> {
    try {
      return this.unwrapData<AgentDocumentsResponse>(
        await ragApi.get(`${this.apiBase}/agents/${agentId}/documents/`)
      )
    } catch (error) {
      log.error(`${this.serviceName} getAgentDocuments failed`, error, { agentId })
      throw error
    }
  }

  /** Leaves the agent with exactly these documents. `permisos` says what it may
   *  do with each; one left out is `leer`. */
  async assignDocuments(
    agentId: string,
    documentIds: number[],
    permisos?: Record<number, DocumentPermission>,
  ): Promise<AgentDocumentsResponse> {
    try {
      return this.unwrapData<AgentDocumentsResponse>(
        await ragApi.post(`${this.apiBase}/agents/${agentId}/documents/`, {
          document_ids: documentIds,
          ...(permisos ? { permisos } : {}),
        })
      )
    } catch (error) {
      log.error(`${this.serviceName} assignDocuments failed`, error, { agentId, documentIds })
      throw error
    }
  }

  async removeDocument(agentId: string, documentId: number): Promise<void> {
    try {
      await ragApi.delete(`${this.apiBase}/agents/${agentId}/documents/${documentId}/`)
    } catch (error) {
      log.error(`${this.serviceName} removeDocument failed`, error, { agentId, documentId })
      throw error
    }
  }

  async listAvailableDocuments(params?: {
    search?: string
    page?: number
    pageSize?: number
    status?: string
    directoryId?: number
    documentTypes?: string[]
    cancelKey?: string
    cancelPrevious?: boolean
  }): Promise<AvailableDocumentsResponse> {
    try {
      const query = {
        search: params?.search?.trim() || undefined,
        page: params?.page ?? 1,
        page_size: params?.pageSize ?? 5,
        status: params?.status ?? 'embedded',
        directory_id: params?.directoryId,
        document_types: params?.documentTypes?.length ? params.documentTypes.join(',') : undefined,
      }
      return this.unwrapData<AvailableDocumentsResponse>(
        await ragApi.get(`${this.apiBase}/documents/available/`, {
          params: query,
          cancelKey: params?.cancelKey,
          cancelPrevious: params?.cancelPrevious,
        })
      )
    } catch (error) {
      log.error(`${this.serviceName} listAvailableDocuments failed`, error, { params })
      throw error
    }
  }

  async listAllAvailableDocuments(params?: {
    search?: string
    status?: string
    directoryId?: number
    documentTypes?: string[]
  }): Promise<AgentAvailableDocument[]> {
    const allItems: AgentAvailableDocument[] = []
    let page = 1
    let hasNext = true

    while (hasNext) {
      const result = await this.listAvailableDocuments({
        search: params?.search,
        status: params?.status,
        directoryId: params?.directoryId,
        documentTypes: params?.documentTypes,
        page,
        pageSize: 100,
      })
      allItems.push(...result.items)
      hasNext = result.has_next
      page = (result.next_page ?? (page + 1))
      if (!result.has_next) break
    }

    return allItems
  }

  async generateAgentCover(prompt: string, style: string): Promise<AgentCoverResponse> {
    try {
      return this.unwrapData<AgentCoverResponse>(await ragApi.post(`${this.apiBase}/agents/generate-cover/`, {
        prompt,
        style,
      }))
    } catch (error) {
      log.error(`${this.serviceName} generateAgentCover failed`, error, { prompt, style })
      throw error
    }
  }

  async listProviderCredentials(): Promise<ProviderCredentialStatus[]> {
    try {
      const response = await ragApi.get(`${this.apiBase}/provider-credentials/`)
      return this.extractResults<ProviderCredentialStatus>(response)
    } catch (error) {
      log.error(`${this.serviceName} listProviderCredentials failed`, error)
      throw error
    }
  }

  async listAvailableModels(): Promise<AgentAvailableModelsResponse> {
    try {
      return this.unwrapData<AgentAvailableModelsResponse>(
        await ragApi.get(`${this.apiBase}/models/available/`)
      )
    } catch (error) {
      log.error(`${this.serviceName} listAvailableModels failed`, error)
      throw error
    }
  }

  async updateProviderCredential(
    provider: ProviderCredentialProvider,
    apiKey: string,
  ): Promise<ProviderCredentialStatus> {
    try {
      return this.unwrapData<ProviderCredentialStatus>(
        await ragApi.put(`${this.apiBase}/provider-credentials/${provider}/`, {
          api_key: apiKey,
        })
      )
    } catch (error) {
      log.error(`${this.serviceName} updateProviderCredential failed`, error, { provider })
      throw error
    }
  }

  async deleteProviderCredential(provider: ProviderCredentialProvider): Promise<ProviderCredentialStatus> {
    try {
      return this.unwrapData<ProviderCredentialStatus>(
        await ragApi.delete(`${this.apiBase}/provider-credentials/${provider}/`)
      )
    } catch (error) {
      log.error(`${this.serviceName} deleteProviderCredential failed`, error, { provider })
      throw error
    }
  }

  async listAgentConnections(agentId: string): Promise<AgentConnection[]> {
    try {
      const response = await ragApi.get(`${this.apiBase}/agents/${agentId}/connections`)
      return this.extractResults<AgentConnection>(response)
    } catch (error) {
      log.error(`${this.serviceName} listAgentConnections failed`, error, { agentId })
      throw error
    }
  }

  async createAgentConnection(
    agentId: string,
    payload: {
      source_key: string
      status?: AgentConnectionStatus
      is_default?: boolean
      config?: Record<string, unknown>
    },
  ): Promise<AgentConnection> {
    try {
      return this.unwrapData<AgentConnection>(
        await ragApi.post(`${this.apiBase}/agents/${agentId}/connections`, payload),
      )
    } catch (error) {
      log.error(`${this.serviceName} createAgentConnection failed`, error, { agentId, payload })
      throw error
    }
  }

  async patchAgentConnection(
    agentId: string,
    connectionId: string,
    payload: Partial<{
      source_key: string
      status: AgentConnectionStatus
      is_default: boolean
      config: Record<string, unknown>
    }>,
  ): Promise<AgentConnection> {
    try {
      return this.unwrapData<AgentConnection>(
        await ragApi.patch(
          `${this.apiBase}/agents/${agentId}/connections/${connectionId}`,
          payload,
        ),
      )
    } catch (error) {
      log.error(`${this.serviceName} patchAgentConnection failed`, error, {
        agentId,
        connectionId,
        payload,
      })
      throw error
    }
  }

  async deleteAgentConnection(agentId: string, connectionId: string): Promise<void> {
    try {
      await ragApi.delete(`${this.apiBase}/agents/${agentId}/connections/${connectionId}`)
    } catch (error) {
      log.error(`${this.serviceName} deleteAgentConnection failed`, error, { agentId, connectionId })
      throw error
    }
  }

  // --- Abilities and Skills (pantallas-abilities-y-skills) ---

  async listAbilities(): Promise<Ability[]> {
    return this.extractResults<Ability>(await ragApi.get(`${this.apiBase}/abilities/`))
  }

  async getAgentAbilities(agentId: string): Promise<Ability[]> {
    return this.extractResults<Ability>(await ragApi.get(`${this.apiBase}/agents/${agentId}/abilities/`))
  }

  /** Leaves the agent with exactly these Abilities. */
  async setAgentAbilities(agentId: string, abilityIds: string[]): Promise<Ability[]> {
    return this.extractResults<Ability>(
      await ragApi.post(`${this.apiBase}/agents/${agentId}/abilities/`, { ability_ids: abilityIds }),
    )
  }

  async listSkills(): Promise<Skill[]> {
    return this.extractResults<Skill>(await ragApi.get(`${this.apiBase}/skills/`))
  }

  async createSkill(payload: SkillPayload): Promise<Skill> {
    return this.unwrapData<Skill>(await ragApi.post(`${this.apiBase}/skills/`, payload))
  }

  /** Changes only what is sent; `agent_ids` and `ability_ids` replace their list. */
  async updateSkill(skillId: string, changes: Partial<SkillPayload>): Promise<Skill> {
    return this.unwrapData<Skill>(await ragApi.patch(`${this.apiBase}/skills/${skillId}/`, changes))
  }

  async deleteSkill(skillId: string): Promise<void> {
    await ragApi.delete(`${this.apiBase}/skills/${skillId}/`)
  }
}

export const agentsService = new AgentsService()
